import React, { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Search, Plus, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuditSummaryCards from "@/components/audits/AuditSummaryCards";
import AuditAttentionPanel from "@/components/audits/AuditAttentionPanel";
import AuditOverviewTab from "@/components/audits/AuditOverviewTab";
import AuditRecordsTab from "@/components/audits/AuditRecordsTab";
import AuditScheduleTab from "@/components/audits/AuditScheduleTab";
import AuditFindingsTab from "@/components/audits/AuditFindingsTab";
import AuditTemplatesTab from "@/components/audits/AuditTemplatesTab";
import AuditFullDrawer from "@/components/audits/AuditFullDrawer";
import AuditNewDialog from "@/components/audits/AuditNewDialog";
import MobileAuditsHome from "@/components/mobile/MobileAuditsHome";

const TABS = [
  { key: "overview",  label: "Overview" },
  { key: "records",   label: "Audit Records" },
  { key: "schedule",  label: "Audit Schedule" },
  { key: "findings",  label: "Findings & Actions" },
  { key: "templates", label: "Templates" },
];

export default function AuditsQA() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "overview");
  const [query, setQuery] = useState("");
  const [summary, setSummary] = useState(null);
  const [actions, setActions] = useState({ total: 0 });
  const [drawerId, setDrawerId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const canManage = ["admin", "manager", "registered_manager", "deputy_manager", "service_manager", "super_admin"].includes(user?.role);

  useEffect(() => { setSp((s) => { s.set("tab", tab); return s; }, { replace: true }); }, [tab, setSp]);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/audits-qa/summary"),
        api.get("/audits-qa/actions"),
      ]);
      setSummary(s.data);
      setActions(a.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  const refresh = () => { setRefreshTick((x) => x + 1); toast.success("Refreshed"); };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* ================ MOBILE (<lg) ================ */}
      <div className="lg:hidden flex-1 min-h-0 flex flex-col" data-testid="audits-qa-mobile-wrap">
        <MobileAuditsHome
          tab={tab}
          setTab={setTab}
          query={query}
          setQuery={setQuery}
          summary={summary}
          actions={actions}
          canManage={canManage}
          refreshTick={refreshTick}
          onRefresh={refresh}
          onNewAudit={() => setNewOpen(true)}
          onOpenAudit={setDrawerId}
          onChanged={() => setRefreshTick((x) => x + 1)}
        />
      </div>

      {/* ================ DESKTOP (>=lg) — untouched ================ */}
      <div className="hidden lg:flex flex-1 min-h-0 flex-col" data-testid="audits-qa-desktop-wrap">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 shrink-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center">
              <ClipboardCheck className="w-4 h-4 text-sky-700" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Governance</div>
              <h1 className="font-heading text-xl font-semibold text-slate-900" data-testid="audits-qa-title">Audits &amp; Quality Assurance</h1>
              <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">Plan audits, monitor quality, manage findings and track improvement actions across your services.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search audits…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="audits-qa-search" className="w-64 h-9 pl-8 text-sm bg-white" />
            </div>
            <Button variant="outline" size="sm" onClick={refresh} data-testid="btn-audits-qa-refresh" className="h-9 text-slate-700">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            {canManage && (
              <Button size="sm" onClick={() => setNewOpen(true)} data-testid="btn-audits-qa-new" className="h-9 bg-sky-700 hover:bg-sky-800">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Audit
              </Button>
            )}
          </div>
        </div>
        {/* Tabs */}
        <div className="mt-4 flex gap-0 border-b border-slate-200 -mb-4 flex-wrap">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`audits-qa-tab-${t.key}`}
                className={`px-4 py-2 text-xs font-medium border-b-2 transition ${active ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary strip */}
      <div className="px-6 pt-4 shrink-0 bg-slate-50 border-b border-slate-200 pb-4 space-y-3">
        <AuditSummaryCards summary={summary} />
        <AuditAttentionPanel actions={actions} onView={() => setTab("findings")} />
      </div>

      {/* Tab body */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50">
        <div className="p-6">
          {tab === "overview"  && <AuditOverviewTab summary={summary} actions={actions} onOpenAudit={setDrawerId} refreshTick={refreshTick} />}
          {tab === "records"   && <AuditRecordsTab search={query} onOpenAudit={setDrawerId} canManage={canManage} refreshTick={refreshTick} />}
          {tab === "schedule"  && <AuditScheduleTab canManage={canManage} refreshTick={refreshTick} onChanged={() => setRefreshTick(x => x + 1)} />}
          {tab === "findings"  && <AuditFindingsTab onOpenAudit={setDrawerId} refreshTick={refreshTick} />}
          {tab === "templates" && <AuditTemplatesTab canManage={canManage} refreshTick={refreshTick} onChanged={() => setRefreshTick(x => x + 1)} />}
        </div>
      </div>
      </div>{/* /audits-qa-desktop-wrap */}

      <AuditFullDrawer auditId={drawerId} onClose={() => setDrawerId(null)} canManage={canManage} onChanged={() => setRefreshTick(x => x + 1)} />
      <AuditNewDialog open={newOpen} onOpenChange={setNewOpen} onSaved={(created) => { setNewOpen(false); setRefreshTick(x => x + 1); if (created?.id) setDrawerId(created.id); }} />
    </div>
  );
}
